import { types } from "mobx-state-tree";
import { flow } from "modules/common/models/flow";
import { Notificator } from "modules/common/models/notificator";
import { Queryable } from "modules/common/models/queryable";
import { SectionCollapser } from "modules/common/models/section-collapser";
import { Transport } from "modules/common/models/transport";
import { buildCacheStorage } from "modules/common/services/cache";
import { apiUrls } from "modules/common/services/communication/urls";
import { nameof } from "modules/common/services/typescript";
import { PlanScale } from "modules/spending/workload-plan/models/plan-scale";
import { PlanDraftApplyResult, PlanDraftView, PlanOperationInput } from "./plan-draft";
import { PlanApproval } from "./plan-approvals";
import { PlanConflict } from "./plan-conflicts";
import { PlanLinkEndInput, PlanLinkEndKind, PlanLinkResponse, PlanLinkTargets, PlanLinkType } from "./plan-dependencies";
import { ProjectPlanResponse } from "./project-plan-builder";
import { ProjectPlanDetail } from "./project-plan-types";

const NAME = "ProjectPlanStore";

/** Сколько шагов помнит «Отменить действие» */
const HISTORY_LIMIT = 50;

/** Операции черновика с сервера — в том виде, в каком их шлёт страница */
function toInputs(view: PlanDraftView | null): PlanOperationInput[] {
    return (view ? view.operations : []).map((operation) => ({
        kind: operation.kind,
        assignmentId: operation.assignmentId || null,
        orderId: operation.orderId || null,
        contentGuid: operation.contentGuid || null,
        milestoneId: operation.milestoneId || null,
        shift: operation.shift || 0,
        day: operation.day || null,
        fromDay: operation.fromDay || null,
    }));
}
const cache = buildCacheStorage(NAME);

const zoomKey = () => nameof((s: ProjectPlanStoreSnapshotType) => s.zoom) as string;
const scaleKey = () => nameof((s: ProjectPlanStoreSnapshotType) => s.scale) as string;
const dayMonthsKey = () => nameof((s: ProjectPlanStoreSnapshotType) => s.monthsOnPageDay) as string;
const weekMonthsKey = () => nameof((s: ProjectPlanStoreSnapshotType) => s.monthsOnPageWeek) as string;
const sortKey = () => nameof((s: ProjectPlanStoreSnapshotType) => s.sort) as string;
const detailKey = () => nameof((s: ProjectPlanStoreSnapshotType) => s.detail) as string;
const busyKey = () => nameof((s: ProjectPlanStoreSnapshotType) => s.showBusy) as string;
const linksKey = () => nameof((s: ProjectPlanStoreSnapshotType) => s.showLinks) as string;

/** Порядок проектов: по номеру (новые сверху) или по ГИПу */
export type ProjectPlanSort = "number" | "gip";

/**
 * Глубина показа настраивается отдельно для каждого масштаба: в дневной сетке месяц
 * занимает втрое больше места, чем в недельной. Верхняя граница совпадает с
 * GetProjectPlanQuery.MAX_MONTHS на бэке.
 */
export const MONTHS_LIMITS: { [key in PlanScale]: { min: number; max: number; deflt: number } } = {
    day: { min: 2, max: 6, deflt: 3 },
    week: { min: 2, max: 12, deflt: 6 },
};

const clampMonths = (scale: PlanScale, value: number) => {
    const limits = MONTHS_LIMITS[scale];
    return Math.min(limits.max, Math.max(limits.min, value));
};

/**
 * Стор страницы «Плановый график работ» отвечает только за транспорт и настройки вида:
 * страница ничего не редактирует, поэтому ни диффа, ни истории правок здесь нет.
 */
export const ProjectPlanStore = types
    .compose(
        Transport,
        Notificator,
        Queryable,
        types.model({
            month: types.number,
            year: types.number,
            zoom: types.number,
            /** Масштаб сетки: колонка-день или неделя целиком */
            scale: types.enumeration<PlanScale>("PlanScale", ["day", "week"]),
            monthsOnPageDay: types.number,
            monthsOnPageWeek: types.number,
            /** Порядок проектов. На Табель План не влияет */
            sort: types.enumeration<ProjectPlanSort>("ProjectPlanSort", ["number", "gip"]),
            /** Во что раскрывается проект: сотрудники или разделы состава проекта */
            detail: types.enumeration<ProjectPlanDetail>("ProjectPlanDetail", ["employees", "sections"]),
            /** Занятость сотрудника на других проектах — приглушённым фоном строки */
            showBusy: types.boolean,
            /** Стрелки связей между работами и разделами */
            showLinks: types.boolean,
            /** Сколько месяцев реально пришло в последнем ответе сервера */
            loadedMonths: types.number,
            loader: types.number,
            collapser: SectionCollapser,
            /**
             * Правки графика — как в Табеле План: копятся на странице и уходят на сервер
             * по «Сохранить». Это операции черновика; предпросмотр считается на месте
             * (computeDraft), в план они попадают только по «Применить черновик».
             */
            operations: types.frozen<PlanOperationInput[]>([]),
            /** Что сейчас лежит в черновике на сервере — от этого считается «не сохранено» */
            savedOperations: types.frozen<PlanOperationInput[]>([]),
            /** Состояния для «Отменить действие» */
            history: types.frozen<PlanOperationInput[][]>([]),
            /** Запрос к черновику в пути */
            draftBusy: types.boolean,
        })
    )
    .views((self) => ({
        get isLoading() {
            return self.loader > 0;
        },

        get transform() {
            return +(self.zoom / 100).toFixed(2);
        },

        get monthsOnPage() {
            return self.scale === "week" ? self.monthsOnPageWeek : self.monthsOnPageDay;
        },

        /**
         * Грузим по максимуму обоих масштабов — тогда переключение день/неделя
         * и уменьшение глубины показа обходятся без запроса к серверу.
         */
        get monthsRequired() {
            return Math.max(self.monthsOnPageDay, self.monthsOnPageWeek);
        },

        /** Есть несохранённые правки */
        get dirty() {
            return JSON.stringify(self.operations) !== JSON.stringify(self.savedOperations);
        },

        /** На сервере есть сохранённый черновик — его можно применить или удалить */
        get hasDraft() {
            return self.savedOperations.length > 0;
        },

        get needsReload() {
            return self.loadedMonths < Math.max(self.monthsOnPageDay, self.monthsOnPageWeek);
        },
    }))
    .actions((self) => ({
        setPeriod(year: number, month: number) {
            self.year = year;
            self.month = month;
        },

        setZoom(value: number) {
            cache.set(zoomKey(), value);
            self.zoom = value;
        },

        setScale(value: PlanScale) {
            cache.set(scaleKey(), value);
            self.scale = value;
        },

        setSort(value: ProjectPlanSort) {
            cache.set(sortKey(), value);
            self.sort = value;
        },

        setDetail(value: ProjectPlanDetail) {
            cache.set(detailKey(), value);
            self.detail = value;
        },

        setShowBusy(value: boolean) {
            cache.set(busyKey(), value);
            self.showBusy = value;
        },

        setShowLinks(value: boolean) {
            cache.set(linksKey(), value);
            self.showLinks = value;
        },

        /** Глубина показа запоминается отдельно для дневного и недельного масштаба */
        setMonthsOnPage(value: number) {
            const months = clampMonths(self.scale, value);

            if (self.scale === "week") {
                cache.set(weekMonthsKey(), months);
                self.monthsOnPageWeek = months;
            } else {
                cache.set(dayMonthsKey(), months);
                self.monthsOnPageDay = months;
            }
        },

        /** Черновик с сервера: правки продолжаются с того места, где их сохранили */
        loadDraft: flow(function* () {
            try {
                const view: PlanDraftView = yield self.transport.get<PlanDraftView>(apiUrls.projectPlan.draft);
                const saved = toInputs(view);

                self.savedOperations = saved;
                self.operations = saved;
                self.history = [];
            } catch (er) {
                self.notify.error(er);
            }
        }),

        /** Правка на странице — как любая правка Табеля План: в историю и в список */
        pushOperation(operation: PlanOperationInput) {
            self.history = [...self.history, self.operations].slice(-HISTORY_LIMIT);
            self.operations = [...self.operations, operation];
        },

        undoOperation() {
            if (!self.history.length) {
                return;
            }

            self.operations = self.history[self.history.length - 1];
            self.history = self.history.slice(0, -1);
        },

        /** «Отмена»: вернуть то, что сохранено */
        cancelOperations() {
            self.operations = self.savedOperations;
            self.history = [];
        },

        /** «Сохранить»: все накопленные правки — одним запросом */
        saveDraft: flow(function* () {
            self.draftBusy = true;

            try {
                const operations = self.operations;
                const view: PlanDraftView = yield self.transport.put<PlanDraftView>(apiUrls.projectPlan.draft, {
                    operations,
                });

                self.savedOperations = operations;
                self.history = [];

                const rejected = view ? view.operations.filter((operation) => !!operation.rejected) : [];

                if (rejected.length) {
                    self.notify.warning(`Не применятся: ${rejected.map((operation) => operation.rejected).join("; ")}`);
                }

                return true;
            } catch (er) {
                self.notify.error(er);
                return false;
            } finally {
                self.draftBusy = false;
            }
        }),

        /** Черновик, пересчитанный сервером, — для окна применения */
        fetchDraftView: flow(function* () {
            try {
                const view: PlanDraftView = yield self.transport.get<PlanDraftView>(apiUrls.projectPlan.draft);
                return view;
            } catch (er) {
                self.notify.error(er);
                return null;
            }
        }),

        /** «Удалить черновик»: и сохранённое, и несохранённое */
        discardDraft: flow(function* () {
            self.draftBusy = true;

            try {
                yield self.transport.delete<boolean>(apiUrls.projectPlan.draft);
                self.operations = [];
                self.savedOperations = [];
                self.history = [];
            } catch (er) {
                self.notify.error(er);
            } finally {
                self.draftBusy = false;
            }
        }),

        /**
         * «Применить черновик». Если план за время правки меняли другие, сервер ничего не
         * применяет и возвращает расхождения — решение за пользователем (force — поверх них).
         */
        applyDraft: flow(function* (force: boolean) {
            self.draftBusy = true;

            try {
                const result: PlanDraftApplyResult = yield self.transport.post<PlanDraftApplyResult>(
                    apiUrls.projectPlan.draftApply,
                    { force }
                );

                if (result && result.applied) {
                    self.operations = [];
                    self.savedOperations = [];
                    self.history = [];
                }

                return result;
            } catch (er) {
                self.notify.error(er);
                return null;
            } finally {
                self.draftBusy = false;
            }
        }),

        /** Открытые запросы на согласование сдвига по связям, которые касаются пользователя */
        fetchApprovals: flow(function* () {
            try {
                const list: PlanApproval[] = yield self.transport.get<PlanApproval[]>(apiUrls.projectPlan.approvals);
                return list || [];
            } catch (er) {
                self.notify.error(er);
                return null;
            }
        }),

        /**
         * Согласовать или отклонить сдвиг. Согласование применяется к плану сразу; если план
         * после запроса меняли другие — сервер вернёт расхождения, не применяя (force — поверх)
         */
        decideApproval: flow(function* (id: string, approve: boolean, force: boolean, comment?: string) {
            self.draftBusy = true;

            try {
                const result: PlanDraftApplyResult = yield self.transport.post<PlanDraftApplyResult>(
                    apiUrls.projectPlan.approvalsDecide,
                    { id, approve, force, comment: comment || "" }
                );

                return result;
            } catch (er) {
                self.notify.error(er);
                return null;
            } finally {
                self.draftBusy = false;
            }
        }),

        /** «Допустимо»: пометка с обязательным комментарием, пишется в историю проектов */
        markConflict: flow(function* (conflict: PlanConflict, comment: string) {
            try {
                yield self.transport.post<boolean>(apiUrls.projectPlan.conflictMarks, {
                    kind: conflict.kind,
                    keys: conflict.keys,
                    employerId: conflict.employerId,
                    orderIds: conflict.orderIds,
                    title: conflict.title,
                    comment,
                });

                return true;
            } catch (er) {
                self.notify.error(er);
                return false;
            }
        }),

        /** Создать связь (без id) или изменить её тип и лаг. Сохраняется сразу, без черновика */
        saveLink: flow(function* (link: {
            id?: string | null;
            type: PlanLinkType;
            lag: number;
            from?: PlanLinkEndInput;
            to?: PlanLinkEndInput;
        }) {
            try {
                const saved: PlanLinkResponse = yield self.transport.post<PlanLinkResponse>(
                    apiUrls.projectPlan.links,
                    link
                );
                return saved;
            } catch (er) {
                // отказ сервера (цикл, дубль) — сообщением, текст причины приходит в ошибке
                self.notify.error(er);
                return null;
            }
        }),

        removeLink: flow(function* (id: string) {
            try {
                yield self.transport.delete<boolean>(apiUrls.projectPlan.removeLink(id));
                return true;
            } catch (er) {
                self.notify.error(er);
                return false;
            }
        }),

        /** Поиск второго конца связи по всем проектам: без orderId — проекты, с ним — его объекты */
        fetchLinkTargets: flow(function* (query: string, orderId: string | null, kind: PlanLinkEndKind) {
            try {
                const result: PlanLinkTargets = yield self.transport.get<PlanLinkTargets>(
                    apiUrls.projectPlan.linkTargets,
                    { params: { query, orderId: orderId || undefined, kind } }
                );
                return result;
            } catch (er) {
                self.notify.error(er);
                return null;
            }
        }),

        /** Снять пометку «Допустимо» */
        unmarkConflict: flow(function* (conflict: PlanConflict) {
            try {
                yield self.transport.post<boolean>(apiUrls.projectPlan.conflictMarksRemove, { keys: conflict.keys });
                return true;
            } catch (er) {
                self.notify.error(er);
                return false;
            }
        }),

        fetch: flow(function* (year?: number, month?: number) {
            self.loader++;

            const monthsCount = self.monthsRequired;

            try {
                const data: ProjectPlanResponse = yield self.transport.get<ProjectPlanResponse>(
                    apiUrls.projectPlan.range,
                    {
                        params: {
                            year: year || self.year,
                            month: month || self.month,
                            monthsCount,
                        },
                    }
                );

                self.loadedMonths = monthsCount;

                return data;
            } catch (er) {
                self.notify.error(er);
                return null;
            } finally {
                self.loader--;
            }
        }),
    }))
    .named(NAME);

export type ProjectPlanStoreType = typeof ProjectPlanStore.Type;
export type ProjectPlanStoreSnapshotType = typeof ProjectPlanStore.SnapshotType;

export const initialState = (): ProjectPlanStoreSnapshotType => {
    const now = new Date();

    return {
        month: now.getMonth() + 1,
        year: now.getFullYear(),
        zoom: cache.get(zoomKey(), 100),
        scale: cache.get<PlanScale>(scaleKey(), "week"),
        monthsOnPageDay: clampMonths("day", cache.get(dayMonthsKey(), MONTHS_LIMITS.day.deflt)),
        monthsOnPageWeek: clampMonths("week", cache.get(weekMonthsKey(), MONTHS_LIMITS.week.deflt)),
        sort: cache.get<ProjectPlanSort>(sortKey(), "number") === "gip" ? "gip" : "number",
        detail: cache.get<ProjectPlanDetail>(detailKey(), "employees") === "sections" ? "sections" : "employees",
        showBusy: cache.get<boolean>(busyKey(), true) !== false,
        showLinks: cache.get<boolean>(linksKey(), true) !== false,
        loadedMonths: 0,
        loader: 0,
        collapser: { opened: {} },
        operations: [],
        savedOperations: [],
        history: [],
        draftBusy: false,
        pureQuery: "",
        query: "",
    };
};
